import { useMemo, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useEventDoc } from "../../lib/useEventDoc";
import { useZones } from "../../lib/useZones";
import { useDraws } from "../../lib/useDraws";
import { applyThemeOverride } from "../../lib/theme";
import { ZoneOverlay } from "../../components/ZoneOverlay";
import { ZoomPanContainer } from "../../components/ZoomPanContainer";
import { LiveDrawCelebration } from "./LiveDrawCelebration";
import { LiveSoldOut } from "./LiveSoldOut";

// Public live view. Critical properties enforced HERE:
//   - Never calls ensureAnonAuth(): completely unauthenticated.
//   - Passes readOnly + no onZoneTap to the overlay: zero tap handlers.
//   - Refuses to render an event in mode:"test".
// Defence in depth: even if any of the above were broken, Firestore rules
// deny client writes and the booking Cloud Functions require an agent code.
export function LiveApp(): JSX.Element {
  const { eventId = "" } = useParams<{ eventId: string }>();
  const [search] = useSearchParams();
  const themeOverride = search.get("theme") as "light" | "dark" | null;
  const compact = search.get("compact") === "1";

  const { event, loaded } = useEventDoc(eventId);
  const { zones } = useZones(eventId);
  const { draws } = useDraws(eventId);

  // Mirror of the DisplayApp reveal-queue pattern: track which revealed
  // draws we've already shown the celebration for, and pop the next pending
  // one when idle. The display gets the dramatic full-screen reveal; the
  // live view gets a calmer 5-second card.
  const [shownIds, setShownIds] = useState<Set<string>>(new Set());
  const [activeReveal, setActiveReveal] = useState<string | null>(null);

  useEffect(() => {
    if (themeOverride === "light" || themeOverride === "dark") {
      applyThemeOverride(themeOverride);
    }
  }, [themeOverride, event?.branding]);

  useEffect(() => {
    if (!event) return;
    document.title = `${event.name} — Live`;
    setMeta("og:title", event.name);
    setMeta("og:description", `Watch ${event.name} fill in real time.`);
    if (event.branding.logoUrl) setMeta("og:image", event.branding.logoUrl);
  }, [event]);

  // Revealed draws only — never expose unrevealed. Also require zoneId so a
  // scheduled-but-not-drawn doc (with revealedAt accidentally set) can't crash.
  const revealedDraws = useMemo(
    () => draws.filter((d) => d.revealedAt && d.zoneId),
    [draws]
  );

  // Queue of revealed draws we haven't yet celebrated on the live view.
  const pendingReveal = useMemo(
    () => revealedDraws.filter((d) => !shownIds.has(d.id)),
    [revealedDraws, shownIds]
  );

  // Pop the next pending reveal as soon as we're idle.
  useEffect(() => {
    if (activeReveal || pendingReveal.length === 0) return;
    setActiveReveal(pendingReveal[0].id);
  }, [activeReveal, pendingReveal]);

  const sold = useMemo(() => zones.filter((z) => z.status === "booked").length, [zones]);
  const total = zones.length;
  const isSoldOut = total > 0 && sold === total;
  const revenue = useMemo(
    () => zones.filter((z) => z.status === "booked").reduce((s, z) => s + z.price, 0),
    [zones]
  );

  const activeDraw = activeReveal ? draws.find((d) => d.id === activeReveal) : null;
  const activeZone = activeDraw?.zoneId ? zones.find((z) => z.id === activeDraw.zoneId) : undefined;

  if (!loaded || !event) {
    return <LiveLoadingSkeleton />;
  }

  if (event.mode === "test") {
    return (
      <div className="h-full grid place-items-center text-center p-12">
        <div>
          <h1 className="font-display text-4xl mb-2">{event.name}</h1>
          <p className="text-fg-muted text-lg">Event not live yet.</p>
        </div>
      </div>
    );
  }

  // Layout note: outer uses `h-full` (NOT `min-h-full`) so the chain has a
  // definite height to resolve against — the ZoneOverlay's inner `h-full`
  // needs that. With `min-h-full` only, the wrapper's height collapses to
  // `auto`, the `flex-1` child also resolves to auto, the grandchild's
  // `h-full` resolves to 100% of auto → 0, and every zone projects through
  // an `imgH` of 0 (= all 200 stacked at one pixel). `min-h-0` on the image
  // container allows the flex item to shrink correctly when winners / legend
  // are also present; otherwise default `min-height: auto` pushes the column
  // taller than its parent and scrolls offscreen.
  return (
    <div className="h-full flex flex-col overflow-hidden" style={{ backgroundColor: "transparent" }}>
      {!compact && (
        <header className="relative flex items-center justify-between px-4 py-3 border-b border-border/40 bg-bg/60 backdrop-blur-sm">
          {/* Accent hairline under the header — same premium edge the
              display surface uses, so the family of surfaces feels related. */}
          <div
            className="absolute left-0 right-0 -bottom-px h-px pointer-events-none"
            aria-hidden
            style={{
              background:
                "linear-gradient(90deg, transparent, rgb(var(--accent) / 0.35), transparent)",
            }}
          />
          <div className="flex items-center gap-3 min-w-0">
            {event.branding.logoUrl ? (
              // Aspect-correct — keep the logo's real proportions.
              <img
                src={event.branding.logoUrl}
                alt=""
                className="h-10 w-auto max-w-[170px] object-contain object-left shrink-0"
              />
            ) : (
              <span
                className="h-10 w-10 rounded-lg border border-border/50 bg-bg-elev/60 grid place-items-center font-display text-sm select-none shrink-0"
                aria-hidden
              >
                <span className="text-accent">JC</span>
              </span>
            )}
            <div className="min-w-0">
              {event.client && (
                <div className="text-[10px] uppercase tracking-[0.25em] text-accent truncate">
                  {event.client}
                </div>
              )}
              <h1 className="font-display text-xl truncate leading-tight">{event.name}</h1>
            </div>
          </div>
          <div className="flex items-center gap-4 text-sm tabular-nums shrink-0">
            <span>
              <span className="font-bold text-accent">{sold}</span>{" "}
              <span className="text-fg-muted">sold</span>
            </span>
            <span className="text-fg-muted">{total - sold} left</span>
          </div>
        </header>
      )}

      {/* Pitch area — pinch / wheel / double-tap zoom. ZoomPanContainer is
          absolutely-positioned over this flex item; ZoneOverlay renders
          identically inside, scaled by the wrapping CSS transform. */}
      <div className="flex-1 min-h-0 relative">
        <ZoomPanContainer>
          <ZoneOverlay
            venueImageUrl={event.venueImageUrl}
            imageAspectRatio={event.imageAspectRatio}
            zones={zones}
            readOnly
            showStateIcons
          />
        </ZoomPanContainer>

        {/* Floating legend pill — overlays the pitch instead of stealing
            vertical space from it. Backdrop-blur lifts it visually off
            whatever's behind. Respects the iOS home-indicator inset. */}
        {!compact && (
          <div
            className="absolute left-1/2 -translate-x-1/2 z-10 inline-flex items-center gap-4 px-4 py-2 rounded-full border border-border/60 bg-bg-elev/80 backdrop-blur shadow-lg text-xs text-fg-muted"
            style={{ bottom: "calc(0.75rem + env(safe-area-inset-bottom, 0px))" }}
          >
            <Legend swatch="bg-z-available" label="Available" />
            <Legend swatch="bg-z-pending" label="Reserving now" pulse />
            <Legend swatch="bg-z-booked" label="Taken" />
          </div>
        )}

        {/* Sold-out celebration mirror — calmer than the big-screen overlay. */}
        {isSoldOut && <LiveSoldOut total={total} revenue={revenue} />}

        {/* Single active winner celebration. Auto-dismisses; on dismiss we
            mark the draw as shown so the next pending one can pop. */}
        {activeDraw && (
          <LiveDrawCelebration
            draw={activeDraw}
            zone={activeZone}
            onDone={() => {
              setShownIds((prev) => {
                const next = new Set(prev);
                next.add(activeDraw.id);
                return next;
              });
              setActiveReveal(null);
            }}
          />
        )}
      </div>

      {/* Calmer winners panel — no dramatic animations here */}
      {!compact && revealedDraws.length > 0 && (
        <div className="px-4 py-3 border-t border-border/40 bg-bg/60 backdrop-blur-sm">
          <div className="text-xs uppercase tracking-widest text-fg-muted mb-2">Winners</div>
          <div className="flex flex-wrap gap-2">
            {revealedDraws.map((d) => {
              const z = zones.find((z) => z.id === d.zoneId);
              return (
                <div
                  key={d.id}
                  className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border border-accent/30 bg-accent/5 text-sm"
                >
                  <span className="font-display text-lg text-accent">{z?.label ?? "?"}</span>
                  <span className="text-fg-muted text-xs">{d.label}</span>
                  <span className="font-medium">{d.winnerName}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function Legend({ swatch, label, pulse }: { swatch: string; label: string; pulse?: boolean }): JSX.Element {
  return (
    <span className="inline-flex items-center gap-2">
      <span className={`w-2.5 h-2.5 rounded-full ${swatch} ${pulse ? "animate-pulse-pending" : ""}`} />
      {label}
    </span>
  );
}

// Skeleton-shimmer loader. Reuses the unused `animate-shimmer` keyframe from
// tailwind.config.ts + the .skeleton-shimmer utility in index.css. Much less
// jarring than a plain "Loading…" line when guests scan a QR.
function LiveLoadingSkeleton(): JSX.Element {
  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex items-center justify-between px-4 py-3 border-b border-border/40">
        <div className="h-6 w-40 rounded skeleton-shimmer animate-shimmer" />
        <div className="h-4 w-24 rounded skeleton-shimmer animate-shimmer" />
      </div>
      <div className="flex-1 min-h-0 relative grid place-items-center">
        <div className="w-[min(90%,640px)] aspect-video rounded-xl skeleton-shimmer animate-shimmer" />
      </div>
    </div>
  );
}

function setMeta(property: string, content: string): void {
  let tag = document.head.querySelector<HTMLMetaElement>(`meta[property="${property}"]`);
  if (!tag) {
    tag = document.createElement("meta");
    tag.setAttribute("property", property);
    document.head.appendChild(tag);
  }
  tag.setAttribute("content", content);
}
